/*

  Dragdrop.ts

  Single page UI for Netskrafl/Explo using the Mithril library

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

  This module implements drag-and-drop functionality for rack tiles,
  using custom handlers for mouse and touch events. Standard HTML5
  drag-and-drop does not offer enough flexibility for our requirements.

*/

interface Point {
  x: number;
  y: number;
}

type DropHandler = (draggedTile: HTMLElement, dropTarget: HTMLElement) => void;

class DragManager {

  // Global drag/drop state. Note that only one drag/drop operation
  // can be active at each time.

  draggedElement: HTMLElement;
  parentElement: HTMLElement | null = null;
  parentRect: DOMRect | null = null;
  offsetX: number = 0;
  offsetY: number = 0;
  centerX: number = 0;
  centerY: number = 0;
  lastX: number = 0;
  lastY: number = 0;
  currentDropTarget: HTMLElement | null = null;
  boundEventHandlers: { [key: string]: (e: MouseEvent | TouchEvent) => void };
  dropHandler: DropHandler;

  getEventCoordinates(e: MouseEvent | TouchEvent): Point {
    if (e instanceof MouseEvent) {
      return { x: e.clientX, y: e.clientY };
    }
    if (e instanceof TouchEvent) {
      const touch = e.touches[0];
      return touch ? { x: touch.clientX, y: touch.clientY } : { x: this.lastX, y: this.lastY };
    }
    return { x: 0, y: 0 };
  }

  static rectContains(rect: DOMRect, p: Point): boolean {
    return (p.x >= rect.left) && (p.x < rect.right) && (p.y >= rect.top) && (p.y < rect.bottom);
  }

  constructor(e: MouseEvent | TouchEvent, dropHandler: DropHandler) {
    e.preventDefault();
    // Note: We use e.currentTarget here, not e.target, as we may be
    // handling events that were originally targeted at a child element
    // (such as the letter or score elements within a tile).
    const dragged = e.currentTarget as HTMLElement;
    const coords = this.getEventCoordinates(e);
    this.lastX = coords.x;
    this.lastY = coords.y;
    this.draggedElement = dragged;
    this.dropHandler = dropHandler;
    this.parentElement = dragged.parentElement;
    this.parentRect = this.parentElement?.getBoundingClientRect() ?? null;
    // Find out the bounding rectangle of the element
    // before starting to apply modifications
    const rect = dragged.getBoundingClientRect();
    const { offsetWidth: originalWidth, offsetHeight: originalHeight } = dragged;
    // Add a class to the dragged element to indicate that it is being dragged.
    // This may change its size and appearance.
    dragged.classList.add("dragging");
    // Make the dragged element an immediate child of the document body,
    // allowing it to be dragged anywhere on the screen and not subject to
    // clipping (overflow: hidden).
    document.body.appendChild(dragged);
    // Find out the dimensions of the element in its dragged state
    const { offsetWidth, offsetHeight } = dragged;
    // Offset of the click or touch within the dragged element
    this.offsetX = coords.x - rect.left + (offsetWidth - originalWidth) / 2;
    this.offsetY = coords.y - rect.top + (offsetHeight - originalHeight) / 2;
    this.centerX = offsetWidth / 2;
    this.centerY = offsetHeight / 2;
    // Create bound event handlers that properly assign 'this'
    this.boundEventHandlers = {
      drag: this.drag.bind(this),
      endDrag: this.endDrag.bind(this),
    };
    const { drag, endDrag } = this.boundEventHandlers;
    if (e instanceof MouseEvent) {
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", endDrag);
    } else if (e instanceof TouchEvent) {
      document.addEventListener("touchmove", drag, { passive: false });
      document.addEventListener("touchend", endDrag);
    }
    // Do an initial position update, as the size of the dragged element
    // may have changed, and it should remain centered
    this.updatePosition(coords.x, coords.y);
  }

  removeDragListeners(): void {
    const { drag, endDrag } = this.boundEventHandlers;
    document.removeEventListener("mousemove", drag);
    document.removeEventListener("mouseup", endDrag);
    document.removeEventListener("touchmove", drag);
    document.removeEventListener("touchend", endDrag);
  }

  elementCenter(clientX: number, clientY: number): Point {
    return {
      x: clientX - this.offsetX + this.centerX,
      y: clientY - this.offsetY + this.centerY,
    };
  }

  findDropTargetAtPoint(x: number, y: number): HTMLElement | null {
    // Look for a drop target element at the given coordinates.
    // A drop target is identified by the 'drop-target' class.
    // To stop the search at a particular type of element,
    // such as a previously played board tile, mark it with
    // the 'not-target' class.
    const { x: centerX, y: centerY } = this.elementCenter(x, y);
    for (const el of document.elementsFromPoint(centerX, centerY)) {
      if (el.classList.contains("drop-target")) {
        return el as HTMLElement;
      }
      if (el.classList.contains("not-target")) {
        // To stop the search at a particular type of element,
        // such as a previously played board tile, mark it with
        // the 'not-target' class.
        return null;
      }
    }
    return null;
  }

  updatePosition(clientX: number, clientY: number): void {
    this.lastX = clientX;
    this.lastY = clientY;
    this.draggedElement.style.left = `${clientX - this.offsetX}px`;
    this.draggedElement.style.top = `${clientY - this.offsetY}px`;
  }

  updateDropTarget(x: number, y: number): void {
    const dropTarget = this.findDropTargetAtPoint(x, y);
    if (dropTarget === this.currentDropTarget) return;
    if (this.currentDropTarget) {
      this.currentDropTarget.classList.remove("over");
    }
    if (dropTarget) {
      // The element is droppable here
      dropTarget.classList.add("over");
    }
    this.currentDropTarget = dropTarget;
  }

  drag(e: MouseEvent | TouchEvent): void {
    e.preventDefault();
    const coords = this.getEventCoordinates(e);
    // Update position for both mouse and touch events
    this.updatePosition(coords.x, coords.y);
    this.updateDropTarget(coords.x, coords.y);
    // If a drop is not allowed, add the "no-drop" class
    // to the dragged element. However, a hack applies:
    // if the dragged element is still over its original
    // parent element, we show the drop as allowed in any case.
    const allowDrop = (
      (this.currentDropTarget !== null) ||
      (this.parentRect && DragManager.rectContains(this.parentRect, coords))
    );
    this.draggedElement.classList.toggle(
      "no-drop",
      !allowDrop,
    );
  }

  resetPosition(): void {
    this.draggedElement.style.left = "";
    this.draggedElement.style.top = "";
    this.draggedElement.classList.remove("dragging", "no-drop");
    if (this.parentElement) {
      // Restore the original parent of the dragged element
      this.parentElement.appendChild(this.draggedElement);
    }
  }

  endDrag(e: MouseEvent | TouchEvent): void {
    e.preventDefault();
    const coords = this.getEventCoordinates(e);
    const dropTarget = this.findDropTargetAtPoint(coords.x, coords.y);
    if (this.currentDropTarget) {
      this.currentDropTarget.classList.remove("over");
    }
    this.removeDragListeners();
    // Avoid flicker by hiding the element while we are manipulating its
    // position in the DOM tree and completing the drop operation
    this.draggedElement.style.visibility = "hidden";
    this.resetPosition();
    if (dropTarget) {
      // Complete the drop operation by calling the provided handler
      this.dropHandler(this.draggedElement, dropTarget);
    }
    setTimeout(() => {
      this.draggedElement.style.visibility = "visible";
    });
  }
}

export const startDrag = (e: MouseEvent | TouchEvent, dropHandler: DropHandler): void => {
  new DragManager(e, dropHandler);
};
